import { useId, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { RiSearchLine } from 'react-icons/ri';

import { CAMPO_DA_ACAO, ConfirmacaoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';
import { findManagersMad } from 'services/dashboard.service';
import { FOCO_VISIVEL } from 'utils/foco';

import { DuplaOption } from '../shared/types';
import { expandManagerToDuplaOptions, suggestedOption } from './succession';

// Modal de seleção da DUPLA de liderança que aprecia a próxima etapa (fluxo MAD,
// MOD-06/SUC-04) — espelho de MinhaEquipe/components/SelectGestor mas listando
// opções-par nomeadas ("GESTOR e PARCEIRO") em vez de um único gestor. A opção
// sugerida (SUC-01, marcada pelo backend) vem pré-selecionada; a busca manual
// consulta gestores_mad e expande cada resultado em opção INDIVIDUAL + uma
// opção-par por parceiro (expandManagerToDuplaOptions) — a dupla é opcional,
// então "GESTOR (individual)" aparece mesmo quando há dupla. onSubmit devolve a
// DuplaOption escolhida — a tradução para {evaluatorId, nonEvaluatorId} é do
// chamador (resolveSelectionWrite).

interface SelectDuplaPlayer {
  task: any;
  // Níveis de gestor aceitos na busca manual (mesma semântica do `cod` do SelectGestor).
  cod?: number[];
  // Opções-par já resolvidas pelo backend (buildDuplaOptions) para o nível corrente.
  duplaOptions?: DuplaOption[];
}

interface IProps {
  player: SelectDuplaPlayer;
  closeModal: () => void;
  onSubmit: (option: DuplaOption) => void;
}

// Nome curto legível de um gestor/parceiro: "NOME_GUERRA (CHAVE)" quando houver,
// com degradação graciosa para nome/_id.
function memberLabel(member: DuplaOption['gestor']): string {
  if (!member) return '';

  const name = member.nome_guerra || member.nome || member._id || '';
  const chave = member.chave ? ` (${member.chave})` : '';

  return `${name}${chave}`;
}

// Rótulo da opção-par: "GESTOR e PARCEIRO" ou "GESTOR (individual)" (opção solo —
// presente também para gestor COM dupla, que pode apreciar sozinho).
function duplaLabel(option: DuplaOption): string {
  const gestorLabel = memberLabel(option.gestor);

  if (isDuplaOption(option)) {
    return `${gestorLabel} e ${memberLabel(option.parceiro)}`;
  }

  return `${gestorLabel} (individual)`;
}

// Identidade estável de uma opção (gestor+parceiro) para chave de lista e comparação
// de seleção — evita depender de igualdade referencial entre listas recriadas.
function optionKey(option: DuplaOption | null | undefined): string {
  if (!option) return '';

  return `${option.gestor?._id ?? ''}::${option.parceiro?._id ?? ''}`;
}

// Uma opção é dupla DE FATO quando há parceiro com `_id`; caso contrário é gestor
// individual (solo). Rege tanto o rótulo da opção quanto o texto do botão de
// confirmação ("Confirmar dupla" × "Confirmar gestor").
function isDuplaOption(option: DuplaOption | null | undefined): boolean {
  return Boolean(option?.parceiro?._id);
}

export default function SelectDupla({ player, closeModal, onSubmit }: IProps) {
  ReactModal.setAppElement('#modal');

  const initialOptions = useMemo<DuplaOption[]>(
    () => (Array.isArray(player.duplaOptions) ? player.duplaOptions : []),
    [player.duplaOptions],
  );

  const [isOpen, setIsOpen] = useState(true);
  const [options, setOptions] = useState<DuplaOption[]>(initialOptions);
  const [selected, setSelected] = useState<DuplaOption | null>(() => suggestedOption(initialOptions));
  const [search, setSearch] = useState<string>('');
  const [searching, setSearching] = useState(false);
  const campoId = useId();
  const avisoId = useId();

  // Busca manual em gestores_mad: cada gestor achado é expandido em opções-par e
  // acrescentado à lista (sem duplicar as já presentes). Nenhuma opção da busca vem
  // sugerida — a sugestão é exclusiva de SUC-01.
  async function handleSearch(e?: React.FormEvent) {
    if (e) e.preventDefault();

    if (!search.trim()) return;

    try {
      setSearching(true);

      const result = await findManagersMad(search, player.cod);

      const expanded = (Array.isArray(result) ? result : []).flatMap((manager: any) =>
        expandManagerToDuplaOptions(manager),
      );

      setOptions(prev => {
        const seen = new Set(prev.map(optionKey));
        const merged = [...prev];

        expanded.forEach(option => {
          const key = optionKey(option);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(option);
          }
        });

        return merged;
      });
    } finally {
      setSearching(false);
    }
  }

  // O X, o Esc e o "Voltar" fecham pelo mesmo caminho.
  function fechar() {
    setIsOpen(false);
    closeModal();
  }

  const selectedKey = optionKey(selected);
  const confirmLabel = isDuplaOption(selected) ? 'Confirmar dupla' : 'Confirmar gestor';

  return (
    <ConfirmacaoDaAcao
      isOpen={isOpen}
      title="Confirme a dupla da próxima etapa"
      size="md"
      onClose={fechar}
      closeLabel="Fechar"
      onConfirm={() => {
        if (selected) onSubmit(selected);
      }}
      confirmLabel={confirmLabel}
      confirmDisabled={!selected}
    >
      <div>
        <label htmlFor={campoId} className="mb-1.5 block text-jj-label font-semibold text-content-soft">
          Dupla responsável por apreciar a realização
        </label>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex-1">
            <input
              id={campoId}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nome ou matrícula"
              aria-describedby={avisoId}
              className={CAMPO_DA_ACAO}
            />
          </form>

          <button
            type="button"
            onClick={() => handleSearch()}
            aria-label="Buscar gestor"
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-jj-md bg-brand-yellow-light text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            <RiSearchLine size={20} aria-hidden="true" />
          </button>
        </div>
      </div>

      <p id={avisoId} className="m-0 text-xs leading-normal text-brand-blue [text-wrap:pretty]">
        Esta dupla é responsável por realizar a apreciação desta entrega? Caso não seja ela, você pode buscar outra dupla
        acima. A dupla age como um: gestor e parceiro apreciam em conjunto.
      </p>

      {searching && (
        <p role="status" className="m-0 animate-pulse text-brand-blue motion-reduce:animate-none">
          Recuperando informações...
        </p>
      )}

      {!searching && !!options.length && (
        <ul className="m-0 flex max-h-[40vh] list-none flex-col gap-2 overflow-y-auto rounded-jj-md bg-surface-100 p-3">
          {options.map((option, index) => {
            const key = optionKey(option);
            const isSelected = key === selectedKey;

            return (
              <li key={`${key}-${index}`}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelected(option)}
                  className={`flex w-full flex-wrap items-center justify-between gap-2 rounded-jj-md border-2 px-4 py-3 text-left ${
                    isSelected
                      ? 'border-brand-blue bg-brand-blue text-brand-white'
                      : 'border-surface-200 bg-brand-white text-brand-blue-dark'
                  } ${FOCO_VISIVEL}`}
                >
                  <span className="text-sm font-semibold">{duplaLabel(option)}</span>
                  {option.suggested ? (
                    <span className="rounded-jj-pill bg-brand-yellow-light px-2.5 py-1 text-[10px] font-bold uppercase text-brand-blue-dark">
                      Sugerida
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!searching && !options.length && (
        <p className="m-0 rounded-jj-md bg-surface-100 p-4 text-center text-content">
          Nenhuma dupla disponível. Experimente buscar por nome e sobrenome ou matrícula.
        </p>
      )}
    </ConfirmacaoDaAcao>
  );
}
